import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Alert from '@cloudscape-design/components/alert'
import Spinner from '@cloudscape-design/components/spinner'
import type { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator'
import { useRun } from '@/hooks/useRuns'
import type { NodeProgress } from '@/lib/types'
import { formatDateTimeWithTZ, humanizeNodeName } from '@/utils/format'

interface Props {
  clientId: string
  runId: string
  errorMessage?: string | null
}

function mapStatus(status: NodeProgress['status']): StatusIndicatorProps['type'] {
  switch (status) {
    case 'complete': return 'success'
    case 'running':  return 'in-progress'
    case 'error':    return 'error'
    case 'pending':  return 'pending'
  }
}

function NodeRow({ node }: { node: NodeProgress }) {
  return (
    <SpaceBetween size="xxs" direction="horizontal">
      <StatusIndicator type={mapStatus(node.status)}>
        {humanizeNodeName(node.name)}
      </StatusIndicator>
      {node.completedAt && (
        <Box variant="small" color="text-body-secondary">
          {formatDateTimeWithTZ(node.completedAt)}
        </Box>
      )}
    </SpaceBetween>
  )
}

export function RunNodeSteps({ clientId, runId, errorMessage }: Props) {
  const { run, loading, error } = useRun(clientId, runId)

  if (loading) {
    return (
      <Box padding={{ left: 'l', vertical: 's' }}>
        <Spinner size="normal" />
      </Box>
    )
  }

  if (error) {
    return (
      <Box padding={{ left: 'l', vertical: 's' }}>
        <Alert type="error">Failed to load run details</Alert>
      </Box>
    )
  }

  return (
    <Box padding={{ left: 'l', vertical: 's' }}>
      <SpaceBetween size="xs">
        {errorMessage && (
          <Alert type="error">{errorMessage}</Alert>
        )}
        {(run?.nodeProgress ?? []).map((node) => (
          <NodeRow key={node.name} node={node} />
        ))}
      </SpaceBetween>
    </Box>
  )
}
